"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import ConfirmationDialog from "../components/ConfirmationDialog"
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CategoryManagement = () => {
  const [selectedItems, setSelectedItems] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("All")
  const [sortBy, setSortBy] = useState("Last 7 Days")
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [categories, setCategories] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [newCategory, setNewCategory] = useState({
    name: "",
    status: "Active"
  })
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [categoryToDelete, setCategoryToDelete] = useState(null)

  useEffect(() => {
    fetchCategories()
  }, [])

  const fetchCategories = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/api/pixel-get-product-categories`,
        {
          headers: {
            'authorization': localStorage.getItem("accessToken"),
            'Content-Type': 'application/json'
          }
        }
      )
      if (response.data && response.data.data) {
        setCategories(response.data.data)
      }
    } catch (err) {
      setError("Failed to fetch categories")
      console.error("Error fetching categories:", err)
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(categories.map((cat) => cat.id))
    } else {
      setSelectedItems([])
    }
  }

  const handleSelectItem = (id) => {
    setSelectedItems((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]))
  }

  const handleAddCategory = async (e) => {
    e.preventDefault()
    try {
      setIsSubmitting(true)
      setError(null)

      const response = await axios.post(
        `${process.env.REACT_APP_API_URI}/api/pixel-create-category`,
        {
          category_name: newCategory.name
        },
        {
          headers: {
            'authorization': localStorage.getItem("accessToken"),
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.data) {
        // Refresh the categories list after successful creation
        await fetchCategories()
        setIsAddDialogOpen(false)
        setNewCategory({ name: "", slug: "", status: "Active" })
        toast.success("Category created successfully!");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create category");

    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setNewCategory(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleEditCategory = async (e) => {
    e.preventDefault()
    try {
      setIsSubmitting(true)
      setError(null)

      const response = await axios.put(
        `${process.env.REACT_APP_API_URI}/api/pixel-edit-product-category/${editingCategory.id}`,
        {
          category_name: editingCategory.category_name
        },
        {
          headers: {
            'authorization': localStorage.getItem("accessToken"),
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.data) {
        // Refresh the categories list after successful update
        await fetchCategories()
        setIsEditDialogOpen(false)
        setEditingCategory(null)
        toast.success("Category updated successfully!");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update category");
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEditClick = (category) => {
    setEditingCategory(category)
    setIsEditDialogOpen(true)
  }

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category)
    setIsDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      setIsSubmitting(true)
      setError(null)

      const response = await axios.delete(
        `${process.env.REACT_APP_API_URI}/api/pixel-delete-product-category/${categoryToDelete.id}`,
        {
          headers: {
            'authorization': localStorage.getItem("accessToken"),
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.data) {
        // Refresh the categories list after successful deletion
        await fetchCategories()
        setIsDeleteDialogOpen(false)
        setCategoryToDelete(null)
        toast.success("Category deleted successfully!");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete category");

    } finally {
      setIsSubmitting(false)
    }
  }

  const filteredCategories = categories.filter(
    (category) =>
      category.category_name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <>
      <ToastContainer  />

      <div className="min-h-screen bg-gray-50 p-2">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-1">Category</h1>
              <p className="text-gray-500">Manage your categories</p>
            </div>
            <button
              onClick={() => setIsAddDialogOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Category
            </button>
          </div>

          {/* Add Category Dialog */}
          {isAddDialogOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
              <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
                <button
                  className="absolute top-2 right-2 text-gray-400 hover:text-gray-700"
                  onClick={() => setIsAddDialogOpen(false)}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <h2 className="text-lg font-semibold mb-4">Add New Category</h2>
                <form onSubmit={handleAddCategory} className="space-y-4">
                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}
                  <div className="space-y-1">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">Category Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={newCategory.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                      placeholder="Enter category name"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  {/* <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label htmlFor="status" className="text-sm font-medium text-gray-700">Status</label>
                    <button
                      type="button"
                      onClick={() => setNewCategory(prev => ({ ...prev, status: prev.status === "Active" ? "Inactive" : "Active" }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                        newCategory.status === "Active" ? "bg-green-500" : "bg-gray-200"
                      }`}
                      disabled={isSubmitting}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          newCategory.status === "Active" ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div> */}
                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setIsAddDialogOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Creating..." : "Add Category"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Edit Category Dialog */}
          {isEditDialogOpen && editingCategory && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
              <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
                <button
                  className="absolute top-2 right-2 text-gray-400 hover:text-gray-700"
                  onClick={() => {
                    setIsEditDialogOpen(false)
                    setEditingCategory(null)
                  }}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <h2 className="text-lg font-semibold mb-4">Edit Category</h2>
                <form onSubmit={handleEditCategory} className="space-y-4">
                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}
                  <div className="space-y-1">
                    <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">Category Name</label>
                    <input
                      type="text"
                      id="edit-name"
                      value={editingCategory?.category_name || ""}
                      onChange={(e) => setEditingCategory(prev => ({ ...prev, category_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                      placeholder="Enter category name"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditDialogOpen(false)
                        setEditingCategory(null)
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Updating..." : "Update Category"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Delete Confirmation Dialog */}
          <ConfirmationDialog
            isOpen={isDeleteDialogOpen}
            onClose={() => {
              setIsDeleteDialogOpen(false)
              setCategoryToDelete(null)
            }}
            onConfirm={handleDeleteConfirm}
            title="Delete Category"
            message={`Are you sure you want to delete the category "${categoryToDelete?.category_name}"? This action cannot be undone.`}
            confirmText="Delete"
            isSubmitting={isSubmitting}
            type="danger"
          />

          {/* Controls */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex justify-between items-center mb-6">
              {/* Search */}
              <div className="relative">
                <svg
                  className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none w-64"
                />
              </div>

              {/* Filters */}
              {/* <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Status</span>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none appearance-none"
                >
                  <option>All</option>
                  <option>Active</option>
                  <option>Inactive</option>
                </select>
                <svg
                  className="w-4 h-4 text-gray-400 -ml-8 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Sort By</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
 className="border border-gray-300 rounded-lg px-3 pr-5 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none appearance-none"                >
                  <option>Last 7 Days</option>
                  <option>Last 30 Days</option>
                  <option>Last 90 Days</option>
                </select>
                <svg
                  className="w-4 h-4 text-gray-400 -ml-8 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div> */}
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 w-12">
                        <input
                          type="checkbox"
                          checked={selectedItems.length === categories.length}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        />
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Category Name</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Created On</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Added By</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCategories.map((category) => (
                      <tr key={category.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 px-4">
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(category.id)}
                            onChange={() => handleSelectItem(category.id)}
                            className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                          />
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-900">{category.category_name}</td>
                        <td className="py-4 px-4 text-sm text-gray-600">{formatDate(category.created_at)}</td>
                        <td className="py-4 px-4 text-sm text-gray-600">{category.added_by}</td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditClick(category)}
                              className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteClick(category)}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-6">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Row Per Page</span>
                  <select
                    value={rowsPerPage}
                    onChange={(e) => setRowsPerPage(Number(e.target.value))}
                    className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <svg
                    className="w-3 h-3 text-gray-400 -ml-6 pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                <span className="text-sm text-gray-600">Entries</span>
              </div>

              <div className="flex items-center gap-2">
                <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <button className="w-8 h-8 rounded bg-orange-500 text-white text-sm font-medium">1</button>

                <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default CategoryManagement
